// ── Entry point ──────────────────────────────────────────────────────────────

import type { ExtensionAPI, ExtensionContext } from '@earendil-works/pi-coding-agent';
import { StatsStore } from './store.js';
import { StatsCollector } from './collector.js';
import { updateStatsFooter } from './footer.js';
import { handleStatsCommand } from './commands.js';

export default function (pi: ExtensionAPI): void {
  let store: StatsStore | null = null;
  let collector: StatsCollector | null = null;

  const refreshFooter = (ctx: ExtensionContext) => {
    if (collector) updateStatsFooter(ctx, collector);
  };

  // ── Register commands ─────────────────────────────────────────────────────

  pi.registerCommand('stats', {
    description: 'Show observability stats (tokens, cost, cache, tools, context)',
    handler: async (args: string, ctx: ExtensionContext) => {
      if (!store || !collector) {
        ctx.ui.notify('Stats not initialized. Wait for session_start.', 'warning');
        return;
      }
      await handleStatsCommand(args, ctx, store, collector);
    },
  });

  // ── Event hooks ────────────────────────────────────────────────────────────

  pi.on('session_start', async (_event, ctx) => {
    const sessionId = ctx.sessionManager.getSessionId();
    store = new StatsStore(sessionId);
    collector = new StatsCollector(store, refreshFooter);
    collector.setContext(ctx);
    collector.onSessionStart(_event, ctx);
  });

  pi.on('message_end', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onMessageEnd(event);
    }
  });

  pi.on('tool_execution_start', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onToolExecutionStart(event);
    }
  });

  pi.on('tool_execution_end', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onToolExecutionEnd(event);
    }
  });

  pi.on('session_before_compact', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onSessionBeforeCompact(event, ctx);
    }
  });

  pi.on('after_provider_response', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onAfterProviderResponse(event);
    }
  });

  pi.on('turn_end', async (event, ctx) => {
    if (collector) {
      collector.setContext(ctx);
      collector.onTurnEnd(event);
    }
  });

  // ── session_shutdown: cleanup stale timers ──────────────────────────────
  pi.on('session_shutdown', async () => {
    if (collector) {
      collector.destroy();
      collector = null;
      store = null;
    }
  });
}